/**
 * Shared mainline conflict rules used by ParlayBetContext (normal parlay)
 * and props prematch board (parlay props).
 *
 * Normal parlay: same group + same period → replace; different period → reject.
 * Props parlay (strictReject): any other pick in the same group for that game → reject.
 */

export type ParlayMarketCategory = 'side' | 'total' | 'team_total';
export type ParlayConflictGroup = 'side' | 'total';

export function getMarketCategory(marketId?: string | null): ParlayMarketCategory | null {
    if (!marketId) return null;

    if (['moneyline', 'point_spread', 'run_line', 'puck_line'].some(m => marketId.includes(m))) {
        return 'side';
    }

    if (['total_points', 'total_runs', 'total_goals'].some(m => marketId.includes(m))) {
        return 'total';
    }

    if (marketId.includes('team_total')) {
        return 'team_total';
    }

    return null;
}

export function getMarketConflictGroup(marketId?: string | null): ParlayConflictGroup | null {
    const category = getMarketCategory(marketId);
    if (!category) return null;
    if (category === 'total' || category === 'team_total') return 'total';
    return category;
}

export function getMarketPeriod(marketId: string): string {
    if (marketId.includes('1st_half')) return '1H';
    if (marketId.includes('2nd_half')) return '2H';
    if (marketId.includes('1st_quarter')) return '1Q';
    if (marketId.includes('2nd_quarter')) return '2Q';
    return 'FG';
}

export type ParlayQueeBet = {
    id_selection?: string;
    gameId?: string | null;
    fixture_id?: string | null;
    odd?: { id?: string; market_id?: string } | null;
};

const isSameGame = (a: ParlayQueeBet, b: ParlayQueeBet) => {
    if (a.gameId && b.gameId && a.gameId === b.gameId) return true;
    if (a.fixture_id && b.fixture_id && a.fixture_id === b.fixture_id) return true;
    if (a.gameId && b.fixture_id && a.gameId === b.fixture_id) return true;
    if (a.fixture_id && b.gameId && a.fixture_id === b.gameId) return true;
    return false;
};

export type ResolveParlayQueeResult =
    | { action: 'toggle_off' }
    | { action: 'reject' }
    | { action: 'replace'; removeSelectionId: string }
    | { action: 'add' };

export type ResolveParlayQueeOptions = {
    /**
     * When true (props board): if the same conflict group already exists for the game,
     * always reject — never replace. Matches FG ML blocking 1H ML for all other mainline picks.
     */
    strictReject?: boolean;
};

/**
 * Mirrors ParlayBetContext.updateQuee decision for one candidate bet.
 * - Same selection id → toggle off
 * - Mainline same game + same conflict group + different period → reject
 * - Mainline same game + same conflict group + same period → replace (unless strictReject)
 * - Special/props (no conflict group) same game + same market_id → replace (unless strictReject → reject)
 * - Otherwise → add
 */
export function resolveParlayQueeAction(
    existingBets: ParlayQueeBet[],
    bet: ParlayQueeBet,
    options: ResolveParlayQueeOptions = {}
): ResolveParlayQueeResult {
    const { strictReject = false } = options;
    const marketId = bet.odd?.market_id;
    if (!marketId) return { action: 'add' };

    if (bet.id_selection && existingBets.some(e => e.id_selection === bet.id_selection)) {
        return { action: 'toggle_off' };
    }

    const conflictGroup = getMarketConflictGroup(marketId);
    const period = getMarketPeriod(marketId);

    if (conflictGroup) {
        const existing = existingBets.find(e => {
            if (!isSameGame(e, bet)) return false;
            return getMarketConflictGroup(e.odd?.market_id) === conflictGroup;
        });

        if (existing?.odd?.market_id) {
            if (strictReject) {
                return { action: 'reject' };
            }
            const existingPeriod = getMarketPeriod(existing.odd.market_id);
            if (existingPeriod !== period) {
                return { action: 'reject' };
            }
            return {
                action: 'replace',
                removeSelectionId: String(existing.id_selection ?? existing.odd.id ?? ''),
            };
        }

        return { action: 'add' };
    }

    const existingSpecial = existingBets.find(
        e => isSameGame(e, bet) && e.odd?.market_id === marketId
    );
    if (existingSpecial) {
        if (strictReject) {
            return { action: 'reject' };
        }
        return {
            action: 'replace',
            removeSelectionId: String(existingSpecial.id_selection ?? existingSpecial.odd?.id ?? ''),
        };
    }

    return { action: 'add' };
}

/**
 * True when selecting this odd would be rejected.
 * - Default (normal): only different period, same conflict group
 * - strictReject (props): any other pick already in the same conflict group for that game
 */
export function isParlayMainlineOddBlocked(
    odd: { id?: string; market_id?: string },
    bets: ParlayQueeBet[],
    gameKeys: { gameId?: string | null; fixtureId?: string | null },
    options: ResolveParlayQueeOptions = {}
): boolean {
    if (!odd?.market_id) return false;
    if (odd.id && bets.some(b => b.id_selection === odd.id)) return false;

    const conflictGroup = getMarketConflictGroup(odd.market_id);
    if (!conflictGroup) return false;

    const period = getMarketPeriod(odd.market_id);
    const candidate: ParlayQueeBet = {
        id_selection: odd.id,
        gameId: gameKeys.gameId,
        fixture_id: gameKeys.fixtureId,
        odd,
    };

    return bets.some(e => {
        if (!isSameGame(e, candidate)) return false;
        if (getMarketConflictGroup(e.odd?.market_id) !== conflictGroup) return false;
        if (!e.odd?.market_id) return false;
        if (options.strictReject) return true;
        return getMarketPeriod(e.odd.market_id) !== period;
    });
}
